import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, FileText, Package,
  AlertTriangle, Briefcase, Shield, Users, Clock, ArrowUpRight, Plus,
  Calendar, Activity, Wallet, Receipt, ArrowDownRight, CircleDot
} from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { useWorkflowStore } from '../store/workflowStore';
import { formatCurrency, formatDate, getRelativeTime, getStatusColor, getStatusLabel, getGreeting, formatNumber } from '../utils/helpers';

const fadeInUp = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

export default function DashboardPage() {
  const { quotations: adminQuotations, customers, products, projects, staff, activityLogs } = useAdminStore();
  const { orders: websiteOrders, quotations: workflowQuotations, invoices, warranties, financeTransactions, getTotalIncome, getTotalExpenses, getTotalMaterialCost, getNetProfit } = useWorkflowStore();
  const navigate = useNavigate();
  const [greeting, setGreeting] = useState('');

  useEffect(() => { setGreeting(getGreeting()); }, []);

  // ===== Live KPI Calculations =====
  const kpiData = useMemo(() => {
    const now = new Date();
    const today = now.toDateString();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    // Use workflowStore financeTransactions for real income data
    // Today's revenue (received payments)
    const todayRevenue = financeTransactions
      .filter(f => new Date(f.date).toDateString() === today && f.type === 'INCOME')
      .reduce((s, f) => s + f.amount, 0);

    // Monthly revenue
    const monthlyRevenue = financeTransactions
      .filter(f => { const d = new Date(f.date); return d.getMonth() === thisMonth && d.getFullYear() === thisYear && f.type === 'INCOME'; })
      .reduce((s, f) => s + f.amount, 0);

    // Previous month for comparison
    const prevMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const prevMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;
    const prevMonthRevenue = financeTransactions
      .filter(f => { const d = new Date(f.date); return d.getMonth() === prevMonth && d.getFullYear() === prevMonthYear && f.type === 'INCOME'; })
      .reduce((s, f) => s + f.amount, 0);
    const revenueChange = prevMonthRevenue > 0 ? ((monthlyRevenue - prevMonthRevenue) / prevMonthRevenue * 100).toFixed(1) : '0';

    // Pending quotations from both stores
    const pendingQuotations = workflowQuotations.filter(q => q.status === 'sent' || q.status === 'draft').length
      + (adminQuotations ? adminQuotations.filter((q: {status: string}) => q.status === 'sent' || q.status === 'draft').length : 0);
    const pendingInstallations = projects.filter(p => p.status === 'installation').length;
    // Low stock
    const lowStockProducts = products.filter(p => (p.stock - p.reservedStock) <= 5);

    // Website Orders (workflow store)
    const todayDate = now.toDateString();
    const newWebsiteOrders = websiteOrders.filter(o => o.status === 'new').length;
    const ordersToday = websiteOrders.filter(o => new Date(o.createdAt).toDateString() === todayDate).length;
    const pendingFollowups = websiteOrders.filter(o => ['new', 'contacted'].includes(o.status)).length;

    // Upcoming installations
    const upcomingInstallations = projects
      .filter(p => p.status === 'approved' || p.status === 'installation')
      .sort((a, b) => new Date(a.estimatedDate || a.createdAt).getTime() - new Date(b.estimatedDate || b.createdAt).getTime())
      .slice(0, 4);

    // Expiring warranties (from workflowStore)
    const expiringWarranties = warranties.filter(w => {
      const daysLeft = (new Date(w.productWarrantyExpiry).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      return daysLeft <= 90 && daysLeft > 0;
    });

    // Recent customers
    const recentCustomers = [...customers]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);

    // Recent payments — from workflowStore invoices
    const recentPayments = invoices
      .flatMap(inv => (inv.payments || []).map(p => ({ ...p, customerName: inv.customerName, invoiceNumber: inv.invoiceNumber })))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);

    // Total from workflow
    const totalRevenue = getTotalIncome();
    const totalExpenses = getTotalExpenses();

    // Monthly data from actual finance transactions
    const monthlyData = [];
    for (let i = 5; i >= 0; i--) {
      const monthIdx = (thisMonth - i + 12) % 12;
      const year = thisMonth - i < 0 ? thisYear - 1 : thisYear;
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const rev = financeTransactions
        .filter(f => { const d = new Date(f.date); return d.getMonth() === monthIdx && d.getFullYear() === year && f.type === 'INCOME'; })
        .reduce((s, f) => s + f.amount, 0);
      const exp = financeTransactions
        .filter(f => { const d = new Date(f.date); return d.getMonth() === monthIdx && d.getFullYear() === year && f.type === 'EXPENSE'; })
        .reduce((s, f) => s + f.amount, 0);
      monthlyData.push({ month: monthNames[monthIdx], revenue: rev, expenses: exp });
    }

    return {
      todayRevenue, monthlyRevenue,
      revenueChange, pendingQuotations, pendingInstallations,
      lowStockProducts, upcomingInstallations, expiringWarranties,
      recentCustomers, recentPayments, totalRevenue,
      totalExpenses, monthlyData,
      newWebsiteOrders, ordersToday, pendingFollowups,
    };
  }, [financeTransactions, workflowQuotations, adminQuotations, customers, products, projects, invoices, warranties, websiteOrders, getTotalIncome, getTotalExpenses]);

  const maxRev = Math.max(...kpiData.monthlyData.map(d => d.revenue));

  const kpis = [
    { label: "Today's Sales", value: formatCurrency(kpiData.todayRevenue), change: '+12.5%', up: true, icon: ShoppingCart, color: 'from-[#7FA86A] to-[#96BE82]' },
    { label: 'Monthly Revenue', value: formatCurrency(kpiData.monthlyRevenue), change: `${Number(kpiData.revenueChange) >= 0 ? '+' : ''}${kpiData.revenueChange}%`, up: Number(kpiData.revenueChange) >= 0, icon: TrendingUp, color: 'from-emerald-400 to-emerald-600' },
    { label: 'Pending Quotations', value: kpiData.pendingQuotations.toString(), change: `${kpiData.pendingQuotations} awaiting`, up: false, icon: FileText, color: 'from-blue-400 to-blue-600' },
    { label: 'Pending Installations', value: kpiData.pendingInstallations.toString(), change: 'Active', up: false, icon: Briefcase, color: 'from-violet-400 to-violet-600' },
    { label: 'Low Stock Alerts', value: kpiData.lowStockProducts.length.toString(), change: kpiData.lowStockProducts.length > 0 ? 'Action needed' : 'All good', up: kpiData.lowStockProducts.length === 0, icon: AlertTriangle, color: kpiData.lowStockProducts.length > 0 ? 'from-red-400 to-red-600' : 'from-gray-400 to-gray-600' },
    { label: 'Total Customers', value: customers.length.toString(), change: `+${kpiData.recentCustomers.length} recent`, up: true, icon: Users, color: 'from-cyan-400 to-cyan-600' },
    { label: 'Warranty Expiring', value: kpiData.expiringWarranties.length.toString(), change: 'Within 90 days', up: false, icon: Shield, color: 'from-rose-400 to-rose-600' },
    { label: 'Active Staff', value: staff.filter(s => s.isActive).length.toString(), change: `${staff.length} total`, up: true, icon: Clock, color: 'from-amber-400 to-amber-600' },
  ];

  const quickActions = [
    { label: 'View Website Orders', path: '/admin/orders', icon: ShoppingCart, color: 'bg-[#F1F6EE] text-[#658C53] hover:bg-[#E8F2E3]' },
    { label: 'Create Quotation', path: '/admin/quotations', icon: FileText, color: 'bg-blue-50 text-blue-600 hover:bg-blue-100' },
    { label: 'Create Invoice', path: '/admin/invoices', icon: Receipt, color: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' },
    { label: 'Add Customer', path: '/admin/customers', icon: Users, color: 'bg-violet-50 text-violet-600 hover:bg-violet-100' },
    { label: 'Add Product', path: '/admin/products', icon: Package, color: 'bg-amber-50 text-amber-600 hover:bg-amber-100' },
    { label: 'Book Site Visit', path: '/admin/projects', icon: Calendar, color: 'bg-cyan-50 text-cyan-600 hover:bg-cyan-100' },
  ];

  return (
    <div className="space-y-6">
      {/* Website Order Alert Banner */}
      {kpiData.newWebsiteOrders > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#F1F6EE] border border-[#D8E7D1] rounded-2xl p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#7FA86A] rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-semibold text-[#2E4422] text-sm">
                🛒 {kpiData.newWebsiteOrders} New Website Order{kpiData.newWebsiteOrders > 1 ? 's' : ''} Waiting
              </p>
              <p className="text-xs text-[#658C53]">
                {kpiData.ordersToday} orders today · {kpiData.pendingFollowups} pending follow-ups
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/orders')}
            className="flex items-center gap-1 px-4 py-2 bg-[#7FA86A] text-white rounded-xl text-xs font-semibold hover:bg-[#658C53] transition-colors"
          >
            View Orders <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      )}

      {/* Header */}
      <motion.div {...fadeInUp}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-[26px] font-semibold text-gray-900 tracking-tight">{greeting}, Admin</h1>
            <p className="text-[13px] text-gray-400 mt-1">Here's your business overview for today</p>
          </div>
          <div className="flex items-center gap-2 text-[13px] text-gray-400 bg-white/80 backdrop-blur-xl px-4 py-2 rounded-xl border border-gray-200/60">
            <Calendar className="w-4 h-4" />
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </motion.div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.3 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5 hover:shadow-lg hover:shadow-gray-200/40 transition-all duration-300 group cursor-default"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${kpi.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                {kpi.up ? (
                  <span className="flex items-center gap-0.5 text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                    <TrendingUp className="w-3 h-3" />{kpi.change}
                  </span>
                ) : (
                  <span className="text-[11px] font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full">{kpi.change}</span>
                )}
              </div>
              <div className="text-[22px] font-bold text-gray-900 tracking-tight">{kpi.value}</div>
              <div className="text-[12px] text-gray-400 mt-0.5 font-medium">{kpi.label}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue Chart */}
        <motion.div {...fadeInUp} transition={{ delay: 0.2 }}
          className="lg:col-span-2 bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-[15px] font-semibold text-gray-900">Revenue vs Expenses</h3>
              <p className="text-[12px] text-gray-400 mt-0.5">Last 6 months performance</p>
            </div>
            <div className="flex items-center gap-4 text-[12px]">
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-[#7FA86A]" />Revenue</div>
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-gray-300" />Expenses</div>
            </div>
          </div>
          <div className="space-y-3">
            {kpiData.monthlyData.map((d) => (
              <div key={d.month} className="flex items-center gap-3">
                <span className="text-[12px] text-gray-400 w-8 font-medium">{d.month}</span>
                <div className="flex-1 flex gap-1">
                  <div className="h-7 rounded-lg bg-gradient-to-r from-[#7FA86A] to-[#96BE82] transition-all duration-500"
                    style={{ width: `${(d.revenue / maxRev) * 100}%` }} />
                  <div className="h-7 rounded-lg bg-gray-200/80 transition-all duration-500"
                    style={{ width: `${(d.expenses / maxRev) * 100}%` }} />
                </div>
                <span className="text-[12px] font-semibold text-gray-700 w-20 text-right">
                  {d.revenue > 0 ? `${(d.revenue / 1000).toFixed(0)}K` : '—'}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Profit Summary */}
        <motion.div {...fadeInUp} transition={{ delay: 0.3 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
          <h3 className="text-[15px] font-semibold text-gray-900 mb-4">Profit Summary</h3>
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-emerald-50/80 border border-emerald-100">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <span className="text-[12px] font-medium text-emerald-600">Total Revenue</span>
              </div>
              <div className="text-[20px] font-bold text-emerald-700">{formatCurrency(kpiData.totalRevenue)}</div>
            </div>
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-gray-400" />
                <span className="text-[12px] font-medium text-gray-500">Total Expenses</span>
              </div>
              <div className="text-[20px] font-bold text-gray-700">{formatCurrency(kpiData.totalExpenses)}</div>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#7FA86A]/5 to-[#7FA86A]/10 border border-[#7FA86A]/20">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4 text-[#7FA86A]" />
                <span className="text-[12px] font-medium text-[#7FA86A]">Net Profit</span>
              </div>
              <div className="text-[20px] font-bold text-[#658C53]">{formatCurrency(kpiData.totalRevenue - kpiData.totalExpenses)}</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions + Activity + Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Quick Actions */}
        <motion.div {...fadeInUp} transition={{ delay: 0.4 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
          <h3 className="text-[15px] font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.label}
                  onClick={() => navigate(action.path)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-all group text-left"
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 ${action.color}`}>
                    <Icon className="w-[18px] h-[18px]" />
                  </div>
                  <span className="text-[13px] font-medium text-gray-700 group-hover:text-gray-900">{action.label}</span>
                  <ArrowUpRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-gray-500 transition-colors" />
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Activity Timeline */}
        <motion.div {...fadeInUp} transition={{ delay: 0.5 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-gray-900">Activity Timeline</h3>
            <span className="text-[11px] font-medium text-gray-400">Recent</span>
          </div>
          <div className="space-y-1">
            {activityLogs.slice(0, 6).map((log) => (
              <div key={log.id} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                  log.status === 'success' ? 'bg-emerald-400' :
                  log.status === 'warning' ? 'bg-amber-400' :
                  log.status === 'error' ? 'bg-red-400' : 'bg-blue-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <span className="text-[13px] font-medium text-gray-900">{log.action}</span>
                  <p className="text-[11px] text-gray-400 mt-0.5 truncate">{log.details}</p>
                </div>
                <span className="text-[10px] text-gray-400 whitespace-nowrap">{getRelativeTime(log.timestamp)}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Upcoming Installations */}
        <motion.div {...fadeInUp} transition={{ delay: 0.6 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-gray-900">Upcoming Installations</h3>
            <button onClick={() => navigate('/admin/projects')} className="text-[12px] text-gray-400 hover:text-gray-600 font-medium">View all</button>
          </div>
          <div className="space-y-1">
            {kpiData.upcomingInstallations.length === 0 ? (
              <div className="py-8 text-center">
                <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-[13px] text-gray-400">No upcoming installations</p>
              </div>
            ) : kpiData.upcomingInstallations.map((project) => (
              <div key={project.id} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-violet-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-gray-900 truncate">{project.name}</div>
                  <div className="text-[11px] text-gray-400">{project.customerName}</div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getStatusColor(project.status)}`}>
                    {getStatusLabel(project.status)}
                  </span>
                  {project.estimatedDate && (
                    <div className="text-[10px] text-gray-400 mt-1">{formatDate(project.estimatedDate)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Recent Customers + Payments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Customers */}
        <motion.div {...fadeInUp} transition={{ delay: 0.7 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-gray-900">Recent Customers</h3>
            <button onClick={() => navigate('/admin/customers')} className="text-[12px] text-gray-400 hover:text-gray-600 font-medium transition-colors">View all</button>
          </div>
          <div className="space-y-1">
            {kpiData.recentCustomers.map((customer) => (
              <div key={customer.id} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-[13px] font-semibold text-gray-600">
                  {customer.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-gray-900 truncate">{customer.name}</div>
                  <div className="text-[11px] text-gray-400">{customer.mobile}</div>
                </div>
                <div className="text-right">
                  <div className="text-[13px] font-semibold text-gray-900">{formatCurrency(customer.totalSpent)}</div>
                  <div className="text-[11px] text-gray-400">{customer.totalProjects} projects</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Payments */}
        <motion.div {...fadeInUp} transition={{ delay: 0.8 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-gray-900">Recent Payments</h3>
            <button onClick={() => navigate('/admin/invoices')} className="text-[12px] text-gray-400 hover:text-gray-600 font-medium transition-colors">View all</button>
          </div>
          <div className="space-y-1">
            {kpiData.recentPayments.length === 0 ? (
              <div className="py-8 text-center">
                <DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-[13px] text-gray-400">No recent payments</p>
              </div>
            ) : kpiData.recentPayments.map((payment, i) => (
              <div key={payment.id || i} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-gray-900 truncate">{payment.customerName}</div>
                  <div className="text-[11px] text-gray-400">{payment.invoiceNumber} · {payment.method.replace('_', ' ')}</div>
                </div>
                <div className="text-right">
                  <div className="text-[13px] font-semibold text-emerald-600">{formatCurrency(payment.amount)}</div>
                  <div className="text-[11px] text-gray-400">{formatDate(payment.date)}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
